import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { useAssets, useAssetTypes } from '../../data/fleet.ts'
import { useAreas } from '../../data/geo.ts'
import { useRpc } from '../../data/people.ts'
import { say, typeLabel, useDocuments, useDocumentVersions, useTaskTypes, useTemplates, useTemplateVersions, type TaskTypeRow } from '../../data/plan.ts'
import {
  applicableAreas, areaChoices, blankTemplate, buildTemplatePublish, draftOfTemplateVersion, langOf, MAX_AREAS, MAX_MACHINES, MAX_QUALS, optInt, procedureRows, templateProblems, templateRows,
  type AreaChoice, type ProcedureRow, type TemplateDraft, type TemplateView,
} from '../../data/planSetup.ts'
import { Banner, Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { ParamField } from '../../ui/ParamField.tsx'
import { CheckRow, digits, Feedback, Problems, useSetupAccess } from './shared.tsx'

interface QType { type_id: string; code: string; label: { fr: string; en: string } }
interface QOverview { types: QType[] }
type Access = ReturnType<typeof useSetupAccess>
type Action = ReturnType<typeof useWorkshopAction>

/** Job templates: the standing definition of a piece of work. Every change is a new immutable version, so a job keeps the wording it was planned with. */
export function TemplatesTab() {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const { org } = useClub()
  const tts = useTaskTypes(); const templates = useTemplates(); const tvs = useTemplateVersions(); const docs = useDocuments(); const docVersions = useDocumentVersions(); const areas = useAreas()
  const assets = useAssets(); const assetTypes = useAssetTypes()
  const access = useSetupAccess('template.publish')
  const quals = useRpc<QOverview>('qualification_overview', { p_org: org.org_id })
  const w = useWorkshopAction()
  const [editing, setEditing] = useState<{ from: TemplateView | null } | null>(null)
  const rows = useMemo(() => templateRows(templates ?? [], tvs ?? [], tts ?? [], docVersions ?? [], lang), [templates, tvs, tts, docVersions, lang])
  const procs = useMemo(() => procedureRows(docs ?? [], docVersions ?? [], tvs ?? [], lang), [docs, docVersions, tvs, lang])
  const choices = useMemo(() => areaChoices(areas ?? [], lang), [areas, lang])
  const machineCodes = useMemo(() => [...new Set([...(assets ?? []).map((a) => a.asset_type_code), ...(assetTypes ?? []).map((x) => x.code)].filter((c): c is string => !!c))].sort(), [assets, assetTypes])
  if (!tts || !templates || !tvs || !docs || !docVersions || !areas) return <p>{t('common.loading')}</p>

  if (editing) {
    return <TemplateForm key={editing.from?.tpl.id ?? 'new'} from={editing.from} taskTypes={tts} procs={procs} choices={choices} quals={quals.data?.types ?? null} qualsDenied={!!quals.error} machineCodes={machineCodes}
      w={w} access={access} onClose={() => setEditing(null)} />
  }
  return (
    <section className="flex flex-col gap-4" data-testid="tpl-list-view">
      <div className="flex flex-col items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{t('planSetup.tpl.title')}</h2>
          <p className="text-sm opacity-75">{t('planSetup.tpl.help')}</p>
        </div>
        {access.permitted && <button className="btn btn-primary !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ from: null }) }} data-testid="tpl-new">{t('planSetup.tpl.new')}</button>}
      </div>
      {!access.permitted && <Banner tone="info">{t('planSetup.notAllowed')}</Banner>}
      <Feedback error={w.error} ok={w.ok} pending={w.pending} />
      {rows.length === 0 && <p className="opacity-70" data-testid="tpl-none">{t('planSetup.tpl.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="tpl-list">
        {rows.map((r) => {
          const { used, latest } = r.procedure
          const archived = !!r.taskType?.archived_at
          return (
            <li key={r.tpl.id} className="card flex flex-col gap-2" data-testid="tpl-row" data-template-id={r.tpl.id} data-task-type={r.tpl.task_type_code}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-lg font-bold" data-testid="tpl-row-title">{say(r.latest.title, lang)}</p>
                <Pill tone="green">{t('planSetup.docs.version', { n: r.latest.version_no })}</Pill>
              </div>
              <p className="text-sm opacity-80">
                {typeLabel(r.taskType, r.tpl.task_type_code, lang)} · {t('planSetup.tpl.areaCount', { count: r.latest.default_area_ids.length })} · {r.latest.required_crew === null ? t('planSetup.tpl.crewUnset') : t('planSetup.tpl.crewLine', { n: r.latest.required_crew })} · {t('planSetup.tpl.groupLine', { n: r.latest.sequence_group })}
              </p>
              <p className="text-sm opacity-80" data-testid="tpl-row-procedure">
                {used ? t('planSetup.tpl.procLine', { title: say(used.title, lang), n: used.version_no }) : r.latest.procedure_document_version_id ? t('planSetup.tpl.procUnknown') : t('planSetup.tpl.procNone')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {used && latest && latest.id !== used.id && <Pill tone="amber">{t('planSetup.tpl.procNewerHint', { n: latest.version_no })}</Pill>}
                {archived && <Pill tone="gray">{t('planSetup.tpl.typeArchived')}</Pill>}
                <Pill tone="gray">{t('planSetup.tpl.versions', { count: r.versions })}</Pill>
              </div>
              {access.permitted && <button className="btn !min-h-11 self-start" disabled={!access.canSave || archived} onClick={() => { w.reset(); setEditing({ from: r }) }} data-testid="tpl-new-version">{t('planSetup.newVersion')}</button>}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function TemplateForm({ from, taskTypes, procs, choices, quals, qualsDenied, machineCodes, w, access, onClose }: {
  from: TemplateView | null; taskTypes: TaskTypeRow[]; procs: ProcedureRow[]; choices: AreaChoice[]; quals: QType[] | null; qualsDenied: boolean; machineCodes: string[]; w: Action; access: Access; onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const creating = from === null
  const ids = useRef({ templateId: from?.tpl.id ?? newId(), versionId: newId() })
  const [init] = useState(() => (from ? draftOfTemplateVersion(from.latest, from.taskType, choices) : { draft: blankTemplate(), dropped: 0 }))
  const [d, setD] = useState<TemplateDraft>(init.draft)
  const [search, setSearch] = useState('')
  const [machine, setMachine] = useState('')
  const tt = taskTypes.find((x) => x.code === d.typeCode)
  const problems = templateProblems(d, tt, creating)
  const procOptions = useMemo(() => procs.map((p) => ({ id: p.latest.id, label: t('planSetup.tpl.procOption', { title: say(p.latest.title, lang), n: p.latest.version_no }) })), [procs, t, lang])
  // the version the template points at, when it is not (or no longer) the newest wording of its document
  const pointed = procs.flatMap((p) => p.history.map((v) => ({ v, p }))).find((x) => x.v.id === d.procedureVersionId)
  const stale = pointed && pointed.v.id !== pointed.p.latest.id ? pointed : undefined

  const kindLabelOf = (code: string): string => t(`areaType.${code}`, { defaultValue: code })
  const eligible = useMemo(() => applicableAreas(choices, tt), [choices, tt])
  const needle = search.trim().toLowerCase()
  const shown = useMemo(() => (needle ? eligible.filter((a) => `${a.label} ${a.code} ${a.context}`.toLowerCase().includes(needle)) : eligible), [eligible, needle])
  const chosen = new Set(d.areaIds)
  const tickAllShown = () => setD((x) => ({ ...x, areaIds: [...new Set([...x.areaIds, ...shown.map((a) => a.id)])].slice(0, MAX_AREAS) }))

  const setParam = (key: string, value: string) => setD((x) => ({ ...x, params: { ...x.params, [key]: value } }))
  const addMachine = () => {
    const code = machine.trim()
    if (code && code.length <= 40 && !d.machines.includes(code) && d.machines.length < MAX_MACHINES) setD({ ...d, machines: [...d.machines, code] })
    setMachine('')
  }
  const changeType = (code: string) => {
    const next = taskTypes.find((x) => x.code === code)
    const ok = new Set(applicableAreas(choices, next).map((a) => a.id))
    setD({ ...d, typeCode: code, params: {}, areaIds: d.areaIds.filter((id) => ok.has(id)) })
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !tt) return
    if (await w.run('job_template.publish@1', buildTemplatePublish(ids.current, d, tt, creating), 'planSetup.tpl.published', { aggregateId: ids.current.templateId })) onClose()
  }
  const openTypes = taskTypes.filter((x) => !x.archived_at).sort((a, b) => a.label[lang].localeCompare(b.label[lang], lang))

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="tpl-form">
      <button type="button" className="self-start text-sm font-semibold underline" onClick={onClose} data-testid="tpl-cancel">← {t('common.back')}</button>
      <h2 className="text-xl font-bold">{from ? t('planSetup.tpl.versionTitle', { title: say(from.latest.title, lang) }) : t('planSetup.tpl.newTitle')}</h2>
      {from && <Banner tone="info">{t('planSetup.tpl.versionHelp', { n: from.latest.version_no, next: from.latest.version_no + 1 })}</Banner>}

      {creating ? (
        <Field label={t('planSetup.tpl.taskType')} htmlFor="tpl-type">
          <select id="tpl-type" className="input" value={d.typeCode} onChange={(e) => changeType(e.target.value)} data-testid="tpl-type">
            <option value="">{t('planSetup.tpl.chooseType')}</option>
            {openTypes.map((x) => <option key={x.id} value={x.code}>{x.label[lang]}</option>)}
          </select>
        </Field>
      ) : (
        <div className="flex flex-col gap-1"><p className="text-sm font-semibold">{t('planSetup.tpl.taskType')}</p><p data-testid="tpl-type-fixed">{typeLabel(from.taskType, from.tpl.task_type_code, lang)}</p><p className="meta">{t('planSetup.tpl.typeFixed')}</p></div>
      )}

      <Field label={t('planSetup.docs.titleFr')} hint={t('planSetup.docs.titleHelp')} htmlFor="tpl-title-fr"><input id="tpl-title-fr" className="input" maxLength={120} value={d.titleFr} onChange={(e) => setD({ ...d, titleFr: e.target.value })} data-testid="tpl-title-fr" /></Field>
      <Field label={t('planSetup.docs.titleEn')} htmlFor="tpl-title-en"><input id="tpl-title-en" className="input" maxLength={120} value={d.titleEn} onChange={(e) => setD({ ...d, titleEn: e.target.value })} data-testid="tpl-title-en" /></Field>
      <Field label={t('planSetup.tpl.instrFr')} htmlFor="tpl-instr-fr"><textarea id="tpl-instr-fr" className="input min-h-28" maxLength={4000} value={d.instrFr} onChange={(e) => setD({ ...d, instrFr: e.target.value })} data-testid="tpl-instr-fr" /></Field>
      <Field label={t('planSetup.tpl.instrEn')} htmlFor="tpl-instr-en"><textarea id="tpl-instr-en" className="input min-h-28" maxLength={4000} value={d.instrEn} onChange={(e) => setD({ ...d, instrEn: e.target.value })} data-testid="tpl-instr-en" /></Field>

      <Field label={t('planSetup.tpl.procedure')} htmlFor="tpl-procedure">
        <select id="tpl-procedure" className="input" value={d.procedureVersionId} onChange={(e) => setD({ ...d, procedureVersionId: e.target.value })} data-testid="tpl-procedure">
          <option value="">{t('planSetup.tpl.procNone')}</option>
          {stale && <option value={stale.v.id}>{t('planSetup.tpl.procOlder', { title: say(stale.v.title, lang), n: stale.v.version_no, latest: stale.p.latest.version_no })}</option>}
          {procOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </Field>
      {stale && (
        <div className="flex flex-col gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="tpl-procedure-newer">
          <p>{t('planSetup.tpl.procNewer', { n: stale.p.latest.version_no })}</p>
          <button type="button" className="btn !min-h-11 self-start" onClick={() => setD({ ...d, procedureVersionId: stale.p.latest.id })} data-testid="tpl-procedure-latest">{t('planSetup.tpl.procUseLatest', { n: stale.p.latest.version_no })}</button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('planSetup.tpl.duration')} hint={t('planSetup.tpl.durationHelp')} htmlFor="tpl-duration" error={optInt(d.duration, 5, 1440).ok ? null : t('planSetup.err.DURATION_INVALID')}>
          <input id="tpl-duration" className="input" inputMode="numeric" value={d.duration} onChange={(e) => setD({ ...d, duration: digits(e.target.value) })} data-testid="tpl-duration" />
        </Field>
        <Field label={t('planSetup.tpl.crew')} hint={t('planSetup.tpl.crewHelp')} htmlFor="tpl-crew" error={optInt(d.crew, 0, 50).ok ? null : t('planSetup.err.CREW_INVALID')}>
          <input id="tpl-crew" className="input" inputMode="numeric" value={d.crew} onChange={(e) => setD({ ...d, crew: digits(e.target.value) })} data-testid="tpl-crew" />
        </Field>
      </div>
      <Field label={t('planSetup.tpl.group')} hint={t('planSetup.tpl.groupHelp')} htmlFor="tpl-group">
        <select id="tpl-group" className="input" value={d.group} onChange={(e) => setD({ ...d, group: e.target.value })} data-testid="tpl-group">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>

      <fieldset className="flex flex-col gap-2" data-testid="tpl-areas">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.tpl.areas')}</legend>
        <p className="meta">{t('planSetup.tpl.areasHelp')}</p>
        {init.dropped > 0 && <Banner tone="warn"><span data-testid="tpl-areas-dropped">{t('planSetup.tpl.areasDropped', { count: init.dropped })}</span></Banner>}
        {!tt ? <p className="text-sm opacity-70">{t('planSetup.tpl.areasPickType')}</p> : (
          <>
            <input type="search" className="input" placeholder={t('planSetup.tpl.areasSearch')} aria-label={t('planSetup.tpl.areasSearch')} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tpl-area-search" />
            <p className="text-sm font-medium" data-testid="tpl-area-count">{t('planSetup.tpl.areasCount', { n: d.areaIds.length, max: MAX_AREAS })}</p>
            <div className="flex gap-2">
              <button type="button" className="btn !min-h-11 flex-1" disabled={shown.length === 0 || d.areaIds.length >= MAX_AREAS} onClick={tickAllShown} data-testid="tpl-area-all">{t('planSetup.tpl.areasAll')}</button>
              <button type="button" className="btn !min-h-11 flex-1" disabled={d.areaIds.length === 0} onClick={() => setD({ ...d, areaIds: [] })} data-testid="tpl-area-none">{t('planSetup.tpl.areasNone')}</button>
            </div>
            {shown.length === 0 && <p className="text-sm opacity-70">{t('planSetup.tpl.areasEmpty')}</p>}
            {shown.length > 0 && <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto rounded-xl border-2 border-slate-300 p-1 dark:border-slate-600" data-testid="tpl-area-list">
              {shown.map((a) => (
                <li key={a.id}>
                  <label className="flex min-h-12 items-start gap-3 rounded-lg px-2 py-1.5">
                    <input type="checkbox" className="mt-0.5 size-6 shrink-0" checked={chosen.has(a.id)} disabled={!chosen.has(a.id) && d.areaIds.length >= MAX_AREAS} data-testid="tpl-area" data-area-code={a.code} data-area-id={a.id}
                      onChange={(e) => setD({ ...d, areaIds: e.target.checked ? [...d.areaIds, a.id] : d.areaIds.filter((x) => x !== a.id) })} />
                    <span className="min-w-0"><span className="font-semibold">{a.label}</span><span className="meta block">{[a.label === kindLabelOf(a.typeCode) ? '' : kindLabelOf(a.typeCode), a.context].filter(Boolean).join(' · ')}</span></span>
                  </label>
                </li>
              ))}
            </ul>}
          </>
        )}
      </fieldset>

      <fieldset className="flex flex-col gap-2" data-testid="tpl-quals">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.tpl.quals')}</legend>
        <p className="meta">{t('planSetup.tpl.qualsHelp')}</p>
        {quals === null ? (qualsDenied ? (d.qualIds.length > 0 && <p className="meta">{t('planSetup.tpl.qualsHidden')}</p>) : <p className="text-sm opacity-70">{t('common.loading')}</p>) : quals.length === 0 ? <p className="text-sm opacity-70">{t('planSetup.tpl.qualsNone')}</p> : (
          <div className="flex flex-wrap gap-2">
            {quals.map((q) => (
              <CheckRow key={q.type_id} checked={d.qualIds.includes(q.type_id)} disabled={!d.qualIds.includes(q.type_id) && d.qualIds.length >= MAX_QUALS} testId={`tpl-qual-${q.code}`}
                onChange={(on) => setD({ ...d, qualIds: on ? [...d.qualIds, q.type_id] : d.qualIds.filter((x) => x !== q.type_id) })}>{q.label[lang]}</CheckRow>
            ))}
          </div>
        )}
      </fieldset>

      <fieldset className="flex flex-col gap-2" data-testid="tpl-machines">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.tpl.machines')}</legend>
        <p className="meta">{t('planSetup.tpl.machinesHelp')}</p>
        <div className="flex flex-wrap gap-2">
          {d.machines.map((c) => (
            <span key={c} className="inline-flex min-h-11 items-center gap-1 rounded-xl border-2 border-slate-300 pl-3 font-mono text-sm dark:border-slate-600" data-testid="tpl-machine" data-code={c}>
              {c}<button type="button" className="grid min-h-11 min-w-11 place-items-center text-lg" aria-label={`${t('common.remove')} ${c}`} onClick={() => setD({ ...d, machines: d.machines.filter((x) => x !== c) })} data-testid={`tpl-machine-remove-${c}`}>×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="input" list="tpl-machine-suggestions" maxLength={40} placeholder={t('planSetup.tpl.machineInput')} aria-label={t('planSetup.tpl.machineInput')} autoCapitalize="none" value={machine} onChange={(e) => setMachine(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMachine() } }} data-testid="tpl-machine-input" />
          <button type="button" className="btn" disabled={!machine.trim() || d.machines.length >= MAX_MACHINES} onClick={addMachine} data-testid="tpl-machine-add">{t('planSetup.tpl.machineAdd')}</button>
        </div>
        <datalist id="tpl-machine-suggestions">{machineCodes.filter((c) => !d.machines.includes(c)).map((c) => <option key={c} value={c} />)}</datalist>
      </fieldset>

      <section className="flex flex-col gap-3" data-testid="tpl-params">
        <h3 className="text-lg font-bold">{t('planSetup.tpl.settings')}</h3>
        <p className="meta">{t('planSetup.tpl.settingsHelp')}</p>
        {!tt || tt.param_defs.length === 0 ? <p className="text-sm opacity-70">{tt ? t('planSetup.tpl.settingsNone') : t('planSetup.tpl.areasPickType')}</p> : tt.param_defs.map((def) => <ParamField key={def.key} def={def} tt={tt} value={d.params[def.key] ?? ''} onChange={(v) => setParam(def.key, v)} idPrefix="tpl-param" lang={lang} />)}
      </section>

      <Problems codes={problems} />
      <Feedback error={w.error} pending={w.pending} />
      <button className="btn btn-primary" disabled={w.busy || !access.canSave || problems.length > 0} data-testid="tpl-save">{w.busy ? t('common.saving') : t('planSetup.tpl.publish')}</button>
      {!access.online && <p className="meta">{t('planSetup.needsNetwork')}</p>}
    </form>
  )
}
